/**
 * Bounding Box Utility Functions
 *
 * A collection of geometric operations for axis-aligned bounding boxes (AABB).
 * These functions are used by the DeclutterManager for spatial calculations.
 *
 * All operations work with pixel coordinates in 2D canvas space.
 *
 * @module declutterManager/bboxUtils
 */

/* ============ BBox Utilities ============ */

import type { AnchorOptions, BBox, RenderedFeature } from './types'

/**
 * Creates a bounding box from a point and size with anchor positioning
 *
 * Converts a center point or top-left point into a full bounding box rectangle.
 * Useful when you have an icon position and need to calculate its spatial footprint.
 *
 * @param x - X coordinate of the anchor point (pixels)
 * @param y - Y coordinate of the anchor point (pixels)
 * @param w - Width of the bounding box (pixels)
 * @param h - Height of the bounding box (pixels)
 * @param options - Anchor positioning options
 * @param options.anchor - Position reference: 'center' or 'topleft' (default: 'center')
 * @param options.padding - Extra padding around the bbox (default: 0)
 *
 * @returns Bounding box with adjusted coordinates
 *
 * @example
 * ```typescript
 * // Icon centered at (500, 300) with 38x38 size
 * const bbox = makeBBoxFromAnchor(500, 300, 38, 38, { anchor: 'center' })
 * // Result: { x: 481, y: 281, w: 38, h: 38 }
 *
 * // With 2px padding
 * const paddedBBox = makeBBoxFromAnchor(500, 300, 38, 38, {
 *   anchor: 'center',
 *   padding: 2
 * })
 * // Result: { x: 479, y: 279, w: 42, h: 42 }
 * ```
 *
 * @example
 * ```
 * Anchor: 'center'           Anchor: 'topleft'
 *
 *    ┌──────────┐            (x,y)
 *    │          │            ┌──────────┐
 *    │   (x,y)  │            │          │
 *    │     +    │            │          │
 *    │          │            │          │
 *    └──────────┘            └──────────┘
 *        w × h                   w × h
 * ```
 */
export function makeBBoxFromAnchor(
  x: number,
  y: number,
  w: number,
  h: number,
  { anchor = 'center', padding = 0 }: AnchorOptions = {},
): BBox {
  const tx = anchor === 'center' ? x - w / 2 : x
  const ty = anchor === 'center' ? y - h / 2 : y

  return {
    x: tx - padding,
    y: ty - padding,
    w: w + padding * 2,
    h: h + padding * 2,
  }
}

/**
 * Checks if two bounding boxes intersect (overlap)
 *
 * Performs axis-aligned bounding box intersection test.
 * Can optionally treat touching edges as intersection.
 *
 * @param a - First bounding box
 * @param b - Second bounding box
 * @param includeTouch - Whether touching edges count as intersection (default: false)
 *
 * @returns True if bounding boxes intersect
 *
 * @example
 * ```typescript
 * const box1 = { x: 0, y: 0, w: 10, h: 10 }
 * const box2 = { x: 5, y: 5, w: 10, h: 10 }
 * const box3 = { x: 10, y: 0, w: 10, h: 10 }
 *
 * rectIntersects(box1, box2)              // true (area overlap)
 * rectIntersects(box1, box3)              // false (only touching)
 * rectIntersects(box1, box3, true)        // true (touching counts)
 * ```
 *
 * @example
 * ```
 * includeTouch = false:        includeTouch = true:
 *
 * ┌──────┬──────┐              ┌──────┬──────┐
 * │  A   │  B   │              │  A   │  B   │
 * └──────┴──────┘              └──────┴──────┘
 *   false (touch only)           true (touch counts)
 *
 * ┌──────┐                     ┌──────┐
 * │  A  ┌┼─────┐               │  A  ┌┼─────┐
 * └─────┼┘  B  │               └─────┼┘  B  │
 *       └──────┘                     └──────┘
 *   true (overlap)                true (overlap)
 * ```
 */
export function rectIntersects(a: BBox, b: BBox, includeTouch = false): boolean {
  const ax2 = a.x + a.w,
    ay2 = a.y + a.h
  const bx2 = b.x + b.w,
    by2 = b.y + b.h

  return includeTouch
    ? a.x <= bx2 && ax2 >= b.x && a.y <= by2 && ay2 >= b.y
    : a.x < bx2 && ax2 > b.x && a.y < by2 && ay2 > b.y
}

/**
 * Calculates the area of intersection between two bounding boxes
 *
 * Computes the overlapping rectangular area. Returns 0 if boxes don't intersect.
 * Used for calculating overlap percentage in declutter logic.
 *
 * @param a - First bounding box
 * @param b - Second bounding box
 *
 * @returns Area of intersection in square pixels (0 if no intersection)
 *
 * @example
 * ```typescript
 * const box1 = { x: 0, y: 0, w: 10, h: 10 }    // Area: 100
 * const box2 = { x: 5, y: 5, w: 10, h: 10 }    // Area: 100
 *
 * const overlap = intersectionArea(box1, box2)  // 25 (5×5 overlap)
 * const percentage = overlap / 100               // 0.25 (25% overlap)
 * ```
 *
 * @example
 * ```
 * Intersection calculation:
 *
 * ┌──────────────┐
 * │      A       │
 * │    ┌─────────┼──────┐
 * │    │█████████│      │
 * └────┼─────────┘      │
 *      │       B        │
 *      └────────────────┘
 *
 * Shaded area (█) = intersection
 * Width:  min(a.x+a.w, b.x+b.w) - max(a.x, b.x)
 * Height: min(a.y+a.h, b.y+b.h) - max(a.y, b.y)
 * ```
 */
export function intersectionArea(a: BBox, b: BBox): number {
  const xo = Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x))
  const yo = Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y))
  return xo * yo
}

/**
 * Checks if one bounding box fully contains another
 *
 * Returns true only if the inner box is completely within the outer box.
 * Used by QuadTree to determine which child node should contain an item.
 *
 * @param outer - The containing bounding box
 * @param inner - The contained bounding box
 *
 * @returns True if outer completely contains inner
 *
 * @example
 * ```typescript
 * const outer = { x: 0, y: 0, w: 100, h: 100 }
 * const inner = { x: 10, y: 10, w: 20, h: 20 }
 * const partial = { x: 50, y: 50, w: 60, h: 60 }
 *
 * rectContains(outer, inner)    // true (fully inside)
 * rectContains(outer, partial)  // false (extends outside)
 * ```
 *
 * @example
 * ```
 * Full containment:           Partial containment:
 *
 * ┌─────────────────┐         ┌─────────────────┐
 * │     Outer       │         │     Outer       │
 * │   ┌────────┐    │         │   ┌─────────────┼──┐
 * │   │ Inner  │    │         │   │  Partial    │  │
 * │   └────────┘    │         │   └─────────────┼──┘
 * └─────────────────┘         └─────────────────┘
 *       true                        false
 * ```
 */
export function rectContains(outer: BBox, inner: BBox): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.w <= outer.x + outer.w &&
    inner.y + inner.h <= outer.y + outer.h
  )
}

/**
 * Calculates the bounding box that contains two bounding boxes
 *
 * Computes the smallest box that completely contains both input boxes.
 * Used when expanding QuadTree boundaries to accommodate new features.
 *
 * @param a - First bounding box
 * @param b - Second bounding box
 *
 * @returns Bounding box containing both inputs
 *
 * @example
 * ```typescript
 * const box1 = { x: 0, y: 0, w: 50, h: 50 }
 * const box2 = { x: 100, y: 100, w: 50, h: 50 }
 *
 * const combined = union(box1, box2)
 * // Result: { x: 0, y: 0, w: 150, h: 150 }
 * ```
 *
 * @example
 * ```
 * Union operation:
 *
 * ┌────────┐                   ┌──────────────────────┐
 * │   A    │                   │      Union           │
 * └────────┘        →          │  ┌────────┐          │
 *              ┌────────┐      │  │   A    │          │
 *              │   B    │      │  └────────┘          │
 *              └────────┘      │          ┌────────┐  │
 *                              │          │   B    │  │
 *                              │          └────────┘  │
 *                              └──────────────────────┘
 * ```
 */
export function union(a: BBox, b: BBox): BBox {
  const x1 = Math.min(a.x, b.x)
  const y1 = Math.min(a.y, b.y)
  const x2 = Math.max(a.x + a.w, b.x + b.w)
  const y2 = Math.max(a.y + a.h, b.y + b.h)
  return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 }
}

/**
 * Expands a bounding box by a margin on all sides
 *
 * Inflates the box uniformly by adding the margin to each edge.
 * Used to add padding around QuadTree boundaries.
 *
 * @param b - Bounding box to inflate
 * @param m - Margin to add on all sides (pixels)
 *
 * @returns Inflated bounding box
 *
 * @example
 * ```typescript
 * const box = { x: 100, y: 100, w: 50, h: 50 }
 * const padded = inflate(box, 10)
 * // Result: { x: 90, y: 90, w: 70, h: 70 }
 * ```
 *
 * @example
 * ```
 * Inflate by margin = 5:
 *
 *     ← 5 →
 *   ┌─────────────┐  ↑
 *   │             │  5
 *   │  ┌───────┐  │  ↓
 *   │  │ Original│ │
 *   │  └───────┘  │
 *   │             │
 *   └─────────────┘
 *   Inflated box (5px margin on all sides)
 * ```
 */
export function inflate(b: BBox, m: number): BBox {
  return { x: b.x - m, y: b.y - m, w: b.w + m * 2, h: b.h + m * 2 }
}

/**
 * Computes the bounding box that contains all rendered features
 *
 * Calculates the smallest box that encompasses all feature bounding boxes.
 * Returns a default 1×1 box if the input array is empty.
 *
 * @param items - Array of rendered features
 * @param padding - Extra padding around the computed bounds (default: 0)
 *
 * @returns Bounding box containing all features
 *
 * @example
 * ```typescript
 * const features: RenderedFeature[] = [
 *   { bbox: { x: 10, y: 10, w: 20, h: 20 } },
 *   { bbox: { x: 50, y: 50, w: 30, h: 30 } },
 * ]
 *
 * const bounds = computeBounds(features)
 * // Result: { x: 10, y: 10, w: 70, h: 70 }
 *
 * const paddedBounds = computeBounds(features, 5)
 * // Result: { x: 5, y: 5, w: 80, h: 80 }
 * ```
 *
 * @example
 * ```
 * Computing bounds from features:
 *
 *   minX                  maxX
 *    ↓                     ↓
 *    ┌─────────────────────┐  ← minY
 *    │  ●                  │
 *    │     ●               │
 *    │        ●            │
 *    │           ●         │
 *    │              ●      │
 *    └─────────────────────┘  ← maxY
 *
 * Bounds: (minX, minY) to (maxX, maxY)
 * ```
 */
export function computeBounds(items: RenderedFeature[], padding = 0): BBox {
  if (items.length === 0) return { x: 0, y: 0, w: 1, h: 1 }

  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity
  for (const it of items) {
    minX = Math.min(minX, it.bbox.x)
    minY = Math.min(minY, it.bbox.y)
    maxX = Math.max(maxX, it.bbox.x + it.bbox.w)
    maxY = Math.max(maxY, it.bbox.y + it.bbox.h)
  }

  return {
    x: Math.floor(minX - padding),
    y: Math.floor(minY - padding),
    w: Math.ceil(maxX - minX + padding * 2),
    h: Math.ceil(maxY - minY + padding * 2),
  }
}
